import api from './api';

/**
 * EDR API 클라이언트 — TRD §5
 *
 * 위협탐지 안내(대시보드·상세)와 운영보고서(종합의견·정책현황·라이선스),
 * 관리자 기능을 모두 담당한다.
 */

export type AgentType = 'PC' | 'SERVER';
export type RiskLevel = '높음' | '중간' | '낮음';

export interface ThreatNoticeRow {
  id: number;
  period: string;
  companyName: string;
  riskLevel: RiskLevel;
  detectedAt: string;
  detectedDate: string;
  detectionScenario: string | null;
  detectionNote: string | null;
  assetIp: string | null;
  parseStatus: 'OK' | 'PARTIAL';
}

export interface ThreatNoticeList {
  rows: ThreatNoticeRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ThreatNoticeDetail extends ThreatNoticeRow {
  riskType: string | null;
  riskTypeNote: string | null;
  assetHost: string | null;
  assetAccount: string | null;
  assetOs: string | null;
  sourceFile: string | null;
  missingFields: string | null;
  /** 살균본 — 원본은 응답에 포함되지 않는다 */
  htmlSanitized: string;
}

export interface ThreatNoticeQuery {
  period?: string;
  companyName?: string;
  riskLevel?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
}

export interface RiskDistribution {
  high: number;
  medium: number;
  low: number;
  total: number;
}

export interface MdrEvent {
  no: number;
  detectedDate: string;
  riskLevel: RiskLevel;
  detectionScenario: string | null;
  detectionNote: string | null;
  assetIp: string | null;
  /** 항상 '메일 발송' 고정 */
  action: string;
}

export interface ReportSummary {
  summaryText: string;
  policyHistory: string;
}

export interface PolicyStatusRow {
  policyGroup: 'EPP' | 'V3' | 'EDR';
  policyCode: string;
  policyName: string;
  policyDesc: string;
  isApplied: boolean;
  /** 이 기간으로 확정 저장된 값인가 */
  isSaved: boolean;
  /** 승계해 온 기간 (isSaved=false일 때만 값이 있다) */
  inheritedFrom: string | null;
}

export interface LicenseSummaryRow {
  affiliate: string;
  /** 공용 계열사 행은 null — 구매는 회사 단위 수기 입력값이다 */
  purchased: number | null;
  installed: number;
  isShared: boolean;
}

export interface LicenseSummary {
  rows: LicenseSummaryRow[];
  total: { purchased: number; installed: number };
}

export interface LicenseAgentRow {
  ip: string | null;
  ipConnected: string | null;
  mac: string | null;
  namePath: string | null;
  agentType: string;
  affiliate: string | null;
  /** 🔒 암호화 컬럼 — 복호화된 값. 정렬 불가 */
  department: string | null;
  name: string | null;
  email: string | null;
  computerName: string | null;
  platformId: string | null;
}

export interface LicenseAgentList {
  rows: LicenseAgentRow[];
  total: number;
  page: number;
  pageSize: number;
  /** 화면이 정렬 헤더를 비활성하는 데 쓴다 */
  unsortableColumns: string[];
}

export interface NoticePreview {
  period: string;
  companyName: string;
  affiliateRaw: string;
  riskLevel: string;
  riskType: string;
  riskTypeNote: string | null;
  /** ⚠️ Date가 UTC ISO로 직렬화된 값이다 — 잘라 쓰지 말고 아래 두 필드를 쓴다 */
  detectedAt: string;
  /** 실제로 저장되는 `detected_date` (YYYY-MM-DD) */
  detectedDate: string;
  /** 화면 표시용 `YYYY-MM-DD HH:mm:ss` */
  detectedAtDisplay: string;
  assetIp: string | null;
  assetHost: string | null;
  assetAccount: string | null;
  assetOs: string | null;
  detectionScenario: string | null;
  detectionNote: string | null;
  sourceFile: string | null;
  parseStatus: 'OK' | 'PARTIAL';
  missingFields: string[];
  contentHash: string;
  htmlSanitized: string;
  isDuplicate: boolean;
}

export interface BuyLicense {
  companyName: string;
  pcAmount: number;
  serverAmount: number;
}

export interface CollectLog {
  period: string;
  status: 'SUCCESS' | 'FAILED';
  rowCount: number | null;
  unmatchedAffiliates: string | null;
  unknownAgentTypes: string | null;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
  triggerType: string;
}

/** PRD [S10] — affiliate → 회사 매핑 규칙(읽기 전용). `weight 0.5`는 공용 계열사 1/2 배분 */
export interface AffiliateMapRow {
  affiliate: string;
  companyName: string;
  weight: number;
  isExcluded: boolean;
}

export interface CompanyOption {
  orgCode: string;
  companyName: string;
}

export const edrService = {
  // ── 회사 목록 ───────────────────────────────────────────────
  // IPT-628: 백엔드는 legacy 'EDR' 을 400 으로 거부한다 — 화면별 pageType 으로 조회
  getCompanies: async (
    pageType: 'EDR_REPORT' | 'EDR_THREAT' = 'EDR_REPORT',
  ): Promise<CompanyOption[]> => {
    const response = await api.get<CompanyOption[]>(`/company/page-type/${pageType}`);
    return response.data;
  },

  // ── 위협탐지 안내 ───────────────────────────────────────────
  /** 회사·기간 미지정 시 인가된 전 계열사·전 기간 */
  getThreatNotices: async (query: ThreatNoticeQuery = {}): Promise<ThreatNoticeList> => {
    const response = await api.get<ThreatNoticeList>('/edr/threat-notices', { params: query });
    return response.data;
  },

  getThreatNoticeDetail: async (id: number): Promise<ThreatNoticeDetail> => {
    const response = await api.get<ThreatNoticeDetail>(`/edr/threat-notices/${id}`);
    return response.data;
  },

  /**
   * 위협탐지 안내가 등록된 기간.
   *
   * ⚠️ 운영보고서의 `getAvailablePeriods`와 다르다 — 위협탐지는 Publish와 무관하다(SC-6).
   */
  getThreatNoticePeriods: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/edr/threat-notices/periods');
    return response.data;
  },

  getDetectedDates: async (companyName: string): Promise<string[]> => {
    const response = await api.get<string[]>('/edr/threat-notices/detected-dates', {
      params: { companyName },
    });
    return response.data;
  },

  // ── 운영보고서 ──────────────────────────────────────────────
  /** 데이터가 있는 기간만. VIEWER에게는 발행된 기간만 온다 */
  getAvailablePeriods: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/edr/report/periods');
    return response.data;
  },

  getRiskDistribution: async (period: string, companyName: string): Promise<RiskDistribution> => {
    const response = await api.get<RiskDistribution>('/edr/report/risk-distribution', {
      params: { period, companyName },
    });
    return response.data;
  },

  getMdrEvents: async (period: string, companyName: string): Promise<MdrEvent[]> => {
    const response = await api.get<MdrEvent[]>('/edr/report/mdr-events', {
      params: { period, companyName },
    });
    return response.data;
  },

  getSummary: async (period: string, companyName: string): Promise<ReportSummary> => {
    const response = await api.get<ReportSummary>('/edr/report/summary', {
      params: { period, companyName },
    });
    return response.data;
  },

  getPolicyStatus: async (period: string, companyName: string): Promise<PolicyStatusRow[]> => {
    const response = await api.get<PolicyStatusRow[]>('/edr/report/policy-status', {
      params: { period, companyName },
    });
    return response.data;
  },

  getPublishStatus: async (
    period: string,
    companyName: string,
  ): Promise<{ isPublished: boolean }> => {
    const response = await api.get<{ isPublished: boolean }>('/edr/report/publish-status', {
      params: { period, companyName },
    });
    return response.data;
  },

  /** agentType으로 PC / 서버 화면이 갈린다 — 빠뜨리면 두 탭이 같은 값을 보여준다 */
  getLicenseSummary: async (
    period: string,
    companyName: string,
    agentType: AgentType,
  ): Promise<LicenseSummary> => {
    const response = await api.get<LicenseSummary>('/edr/report/license-summary', {
      params: { period, companyName, agentType },
    });
    return response.data;
  },

  getLicenseAgents: async (params: {
    period: string;
    companyName: string;
    agentType: AgentType;
    page?: number;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<LicenseAgentList> => {
    const response = await api.get<LicenseAgentList>('/edr/report/license-agents', { params });
    return response.data;
  },

  // ── 관리자 — 위협탐지 HTML ──────────────────────────────────
  previewThreatNotice: async (html: string): Promise<NoticePreview> => {
    const response = await api.post<NoticePreview>('/edr/admin/threat-notices/preview', { html });
    return response.data;
  },

  registerThreatNotice: async (html: string): Promise<ThreatNoticeRow> => {
    const response = await api.post<ThreatNoticeRow>('/edr/admin/threat-notices', { html });
    return response.data;
  },

  uploadThreatNotice: async (file: File): Promise<ThreatNoticeRow> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ThreatNoticeRow>(
      '/edr/admin/threat-notices/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  getThreatNoticeRaw: async (id: number): Promise<{ html: string }> => {
    const response = await api.get<{ html: string }>(`/edr/admin/threat-notices/${id}/raw`);
    return response.data;
  },

  updateThreatNotice: async (id: number, html: string): Promise<ThreatNoticeRow> => {
    const response = await api.put<ThreatNoticeRow>(`/edr/admin/threat-notices/${id}`, { html });
    return response.data;
  },

  deleteThreatNotice: async (id: number): Promise<{ deleted: number }> => {
    const response = await api.delete<{ deleted: number }>(`/edr/admin/threat-notices/${id}`);
    return response.data;
  },

  // ── 관리자 — 운영보고서 ────────────────────────────────────
  /**
   * 요약·정책 변경 이력 저장. **보낸 필드만 갱신된다** —
   * 요약 저장이 변경 이력을 지우지 않는다.
   */
  saveSummary: async (
    period: string,
    companyName: string,
    input: { summaryText?: string; policyHistory?: string },
  ): Promise<void> => {
    await api.put('/edr/admin/report/summary', { period, companyName, ...input });
  },

  savePolicyStatus: async (
    period: string,
    companyName: string,
    items: Array<{ policyCode: string; isApplied: boolean }>,
  ): Promise<{ saved: number }> => {
    const response = await api.put<{ saved: number }>('/edr/admin/report/policy-status', {
      period,
      companyName,
      items,
    });
    return response.data;
  },

  // ── 관리자 — 라이선스 ──────────────────────────────────────
  getBuyLicenses: async (): Promise<BuyLicense[]> => {
    const response = await api.get<BuyLicense[]>('/edr/admin/license/buy');
    return response.data;
  },

  saveBuyLicenses: async (items: BuyLicense[]): Promise<{ saved: number }> => {
    const response = await api.put<{ saved: number }>('/edr/admin/license/buy', { items });
    return response.data;
  },

  /** 매핑은 DB 데이터다 — 화면에 하드코딩하면 매핑 추가마다 배포가 필요해진다 (§4.1.1) */
  getAffiliateMap: async (): Promise<AffiliateMapRow[]> => {
    const response = await api.get<AffiliateMapRow[]>('/edr/admin/license/affiliate-map');
    return response.data;
  },

  getCollectStatus: async (period?: string): Promise<CollectLog | null> => {
    const response = await api.get<CollectLog | null>('/edr/admin/license/collect-status', {
      params: period ? { period } : undefined,
    });
    return response.data;
  },
};

export default edrService;
